/**
 * The agent chat — the design's «AI Agent» launcher under the header, and, open, a column on the left
 * of the window (the stage narrows beside it): the transcript, the field where the user tells an agent
 * what to do in the project — the AI Agent hands the work to its subagents — and the settings behind it (model, own
 * key, provider, local agent, the bridge for external agents, training mode).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { agentSession, chatTitle, projectUnchanged, type AgentSession, type AgentSessionState, type ChatPart, type ChatTurn, type SavedChat } from '../../agent/runtime';
import { AgentBridge, BRIDGE_URL } from '../../agent/bridge';
import { agentModel, LOCAL_AGENT_URL, LOCAL_MODEL_CHOICES, MODEL_CHOICES, PROVIDER_CHOICES, type ProviderKind } from '../../agent/agents';
import { DEEPSEEK_BASE_URL, DEEPSEEK_MODELS } from '../../agent/openaiProvider';
import { editorAccess } from '../../agent/editorAccess';
import { blobToBase64, captureViewImage } from '../../agent/toolRuntime';
import { store } from '../../state/store';
import { useStore } from '../useStore';
import { usePresence } from '../usePresence';
import { AGENT_WIDTH, clampAgentWidth, dragAgentWidth, fitAgentWidth, storeAgentWidth, storedAgentWidth } from './agentColumn';
import { Icon } from '../Icon';
import { agentCursor, type CursorState } from '../../agent/cursor';
import { TOOL_PROGRESS } from '../../agent/toolLabels';
import { ActionMenu, SelectDropdown } from '../Dropdown';
import { demoBots } from '../../collaboration/demoBots';
import { roleById, roleNamed } from '../../agent/roles';
import { ParticipantPointer } from '../collaboration/CollaborationOverlay';
import { DemoControls } from '../collaboration/DemoControls';
import { cultureName, isTextOf, plural, t } from '../../i18n/I18n';
import { rich } from '../../i18n/rich';

function useAgentSession(session: AgentSession): AgentSessionState {
  return useSyncExternalStore(session.subscribe, session.get, session.get);
}

let bridge: AgentBridge | null = null;
function bridgeFor(session: AgentSession): AgentBridge {
  bridge ??= new AgentBridge({
    onStatus: status => session.setBridgeStatus(status),
    onCall: (name, input) => { if (session.get().status !== 'running') agentCursor.setAgent(t('#AgentExternalName')); return session.noteExternal(name, input); },
    onIdle: () => session.closeExternalTurn(),
  });
  return bridge;
}

const elapsedLabel = (turn: ChatTurn) => turn.finishedAt ? durationLabel(turn.finishedAt - turn.startedAt) : '';
/** «12 с», «2 мин 43 с»: how long an answer took, as the design's «Worked 2m 43s» says it. */
function durationLabel(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1000)), minutes = Math.floor(seconds / 60);
  return minutes ? seconds % 60 ? t('#AgentPanelMinutesSeconds', minutes, seconds % 60) : t('#AgentPanelMinutes', minutes) : t('#AgentPanelSeconds', seconds);
}
/** «сегодня 14:35», «вчера 09:12», «12 сентября 14:35»: the stamp over a stretch of the conversation. */
function stampLabel(at: number, now = Date.now()): string {
  const date = new Date(at), time = date.toLocaleTimeString(cultureName(), { hour: '2-digit', minute: '2-digit' });
  const day = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round((day(new Date(now)) - day(date)) / 86_400_000);
  return days === 0 ? t('#AgentPanelStampToday', time) : days === 1 ? t('#AgentPanelStampYesterday', time) : t('#AgentPanelStampDate', date.toLocaleDateString(cultureName(), { day: 'numeric', month: 'long' }), time);
}
/** A stamp opens the conversation and every stretch that starts half an hour after the last one. */
const STAMP_GAP_MS = 30 * 60_000;
/** The card colour of an agent outside the roster: an old saved chat's agent, the bridge's. */
const NEUTRAL_AGENT_COLOR = '#8a8f98';
const stepsLabel = (count: number) => plural('#AgentPanelSteps', count);

/**
 * The launcher over the stage while the chat is closed: the «ИИ-агент» button, and under it the
 * status strip of the last answer — the step running now, then how it ended. Either opens the chat.
 */
export function AgentLauncher() {
  const session = agentSession();
  const state = useAgentSession(session);
  const running = state.status === 'running';
  // Always there: without a model the chat runs the agents' simulation and its health check (docs/agents.md).
  const lastTurn = [...state.turns].reverse().find(turn => turn.role !== 'user');
  const successful = lastTurn?.status === 'done' && !lastTurn.parts.some(part => part.kind === 'tool' && part.status === 'error');
  return <div className="agent-launch">
    <button type="button" className="agent-launch-button" data-running={running}
      aria-description={running ? t('#AgentPanelRunningOpen') : undefined} data-tip={running ? t('#AgentPanelRunningOpen') : t('#WebTipOpenAgent')} onClick={() => session.setOpen(true)}>
      <span><Icon name="aiStar" size={20} /><span>{t('#AgentPanelAiAgent')}</span></span><Icon name={running ? 'spin' : 'openWindow'} size={20} />
    </button>
    {lastTurn && <button type="button" className={`agent-status is-${lastTurn.status}`} onClick={() => session.setOpen(true)} title={t('#AgentPanelOpenChat')}>
      <span className="agent-steps-state">{lastTurn.status === 'streaming' ? <span className="agent-spinner" /> : <Icon name={successful ? 'stepCheck' : 'info'} size={successful ? 16 : 14} />}</span>
      <span className="agent-status-text"><strong>{lastTurn.agentName ?? t('#AgentPanelAiAgent')}</strong> · {turnStatusLine(lastTurn)}</span>
    </button>}
  </div>;
}

/**
 * Always mounted beside the stage: it keeps the bridge for external agents and the local agent's
 * probe alive whether the chat is open or not, and shows the chat column while it is open (with a
 * short exit while it closes). The attachments wait here, so closing the chat does not drop them.
 */
export function AgentDock() {
  const session = agentSession();
  const state = useAgentSession(session);
  const [attachments, setAttachments] = useState<string[]>([]);
  const { mounted, closing } = usePresence(state.open, 160);

  useEffect(() => {
    const link = bridgeFor(session);
    if (state.settings.bridge || state.settings.localAgent) link.enable(); else link.disable();
  }, [session, state.settings.bridge, state.settings.localAgent]);

  // The local agent server is polled while the mode is on, so the chat says whether it is up.
  useEffect(() => {
    if (!state.settings.localAgent) { void session.probeLocalAgent(); return; }
    void session.probeLocalAgent();
    const timer = setInterval(() => { void session.probeLocalAgent(); }, 8000);
    return () => clearInterval(timer);
  }, [session, state.settings.localAgent, state.settings.localAgentUrl, state.open]);

  return mounted ? <AgentColumn session={session} state={state} closing={closing} attachments={attachments} setAttachments={setAttachments} /> : null;
}

function AgentColumn({ session, state, closing, attachments, setAttachments }: { session: AgentSession; state: AgentSessionState; closing: boolean;
  attachments: string[]; setAttachments: (update: (list: string[]) => string[]) => void }) {
  const scene = useStore(s => ({ hasRooms: !!s.scene?.levels[s.activeLevelIndex]?.rooms.length, mode: s.mode }));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const thread = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const running = state.status === 'running';
  const agent = session.agent;
  const draft = state.draft;
  const canSend = !running && (draft.trim().length > 0 || attachments.length > 0);

  useLayoutEffect(() => {
    const element = textarea.current;
    if (!element) return;
    element.style.height = '0px';
    element.style.height = `${Math.min(200, Math.max(48, element.scrollHeight))}px`;
  }, [draft]);

  useLayoutEffect(() => {
    const element = thread.current;
    if (element && stickToBottom.current) element.scrollTop = element.scrollHeight;
  }, [state.turns]);

  // The column opens with the field ready to type into, unless it opened under a finger (no keyboard pops up uninvited).
  useEffect(() => {
    if (closing || matchMedia('(pointer: coarse)').matches) return;
    textarea.current?.focus({ preventScroll: true });
  }, [closing]);

  const close = () => { session.setOpen(false); textarea.current?.blur(); };
  const submit = () => {
    if (!canSend) return;
    const images = attachments;
    setAttachments(() => []);
    stickToBottom.current = true;
    void session.send(draft, images);
  };
  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); submit(); }
    else if (event.key === 'Escape') { event.preventDefault(); if (settingsOpen) setSettingsOpen(false); else close(); }
  };
  const capture = async () => {
    const editor = editorAccess();
    if (!editor || capturing) return;
    setCapturing(true);
    try {
      const picture = await captureViewImage(editor, 1024);
      if (!picture) return;
      const dataUrl = `data:image/jpeg;base64,${await blobToBase64(picture.blob)}`;
      setAttachments(list => [...list, dataUrl]);
    } finally { setCapturing(false); }
  };
  // The design's prompts, short enough for one line of the column.
  const suggestions = scene.hasRooms
    ? [t('#AgentPanelSuggestFurnish'), t('#AgentPanelSuggestWindow'), t('#AgentPanelSuggestFloor'), scene.mode === 'plan' ? t('#AgentPanelSuggest3D') : t('#AgentPanelSuggestRender')]
    : [t('#AgentPanelSuggestRoom'), t('#AgentPanelSuggestBedroom'), t('#AgentPanelSuggestProjects')];
  const modelReady = session.modelAvailable();
  const bridgeStatus = state.settings.bridge || state.settings.localAgent ? state.bridge : null;
  const localStatus = state.settings.localAgent ? state.local : null;
  const modelLabel = session.effectiveProvider() === 'openai' && !state.settings.localAgent ? `${state.settings.openai?.baseUrl && !state.settings.openai.baseUrl.includes('deepseek') ? 'API' : 'DeepSeek'} · ${session.modelFor(agent)}` : state.settings.localAgent ? (state.settings.localModel ? `Claude Code · ${LOCAL_MODEL_CHOICES.find(choice => choice.id === state.settings.localModel)?.label ?? state.settings.localModel}` : 'Claude Code') : MODEL_CHOICES.find(choice => choice.id === agentModel(agent, state.settings))?.label ?? agentModel(agent, state.settings);
  // The chat is named by its first request, the way the chats list names it; an empty one is a new chat.
  const firstRequest = state.turns.find(turn => turn.role === 'user')?.parts.find((part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text')?.text.trim();
  const title = state.chats.find(chat => chat.id === state.chatId)?.title ?? (firstRequest ? chatTitle(firstRequest) : t('#AgentPanelNewChat'));
  const listView = state.view === 'list';
  const demo = useSyncExternalStore(demoBots.subscribe, demoBots.current, demoBots.current);

  return <section className={`agent-dock${closing ? ' is-closing' : ''}`} data-status={state.status} data-view={state.view} aria-label={t('#AgentPanelAgentChat')} inert={closing}>
    <header className="agent-head">
      {listView ? <h2 className="agent-title">{t('Chats')}</h2> : <>
        <button type="button" className="agent-mini agent-back" aria-label={t('#AgentPanelBackToChats')} title={t('Chats')} onClick={() => session.setView('list')}><Icon name="back" size={20} /></button>
        <h2 className="agent-title" title={`${agent.name} · ${modelLabel}`}>{title}</h2>
      </>}
      <button type="button" className="agent-mini agent-collapse" aria-label={t('#AgentPanelCollapseChat')} title={t('#AgentPanelCollapseEsc')} onClick={close}><Icon name="sidebar" size={22} /></button>
    </header>
    {!listView && demo && <DemoControls demo={demo} placement="chat" />}
    {listView && <ChatList session={session} state={state} onSettings={() => setSettingsOpen(true)} onHealth={() => void session.checkHealth()} />}
    {!listView && (localStatus || bridgeStatus) && <div className="agent-links">
      {localStatus && <span className={`agent-bridge-pill is-${localStatus.state === 'ready' ? 'connected' : localStatus.state === 'offline' ? 'error' : 'connecting'}`} title={localStatus.detail ?? session.localAgentUrl}><Icon name="sparkles" size={13} />{localStatus.state === 'ready' ? t('#AgentPanelLocalReady') : localStatus.state === 'offline' ? t('#AgentPanelLocalOffline') : t('#AgentPanelLocalChecking')}</span>}
      {bridgeStatus && <span className={`agent-bridge-pill is-${bridgeStatus.state}`} title={bridgeStatus.detail ?? BRIDGE_URL}><Icon name="external" size={13} />{bridgeStatus.state === 'connected' ? t('#AgentPanelMcpConnected') : bridgeStatus.state === 'connecting' ? t('#AgentPanelMcpConnecting') : t('#AgentPanelMcpOffline')}</span>}
    </div>}
    {!listView && <div className="agent-thread" ref={thread} onScroll={event => { const el = event.currentTarget; stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40; }}>
      {!state.turns.length ? <div className="agent-empty">
        <p>{rich('#AgentPanelEmptyIntro', <strong>{agent.name}</strong>, agent.description)}</p>
        <div className="agent-chips">{suggestions.map(text => <button key={text} type="button" className="chip" onClick={() => { session.setDraft(text); textarea.current?.focus(); }}>{text}</button>)}</div>
        <p className="agent-fineprint">{t('#AgentPanelUndoEach')}<br />{t('#AgentPanelUndoAll')}</p>
        {/* Without a model the agents still show their work: the simulation, and the check of what an agent run stands on. */}
        <div className={'agent-sim' + (modelReady ? '' : ' is-offline')}>
          <p>{modelReady ? t('#AgentPanelModelCheck', modelLabel) : <><strong>{t('#AgentPanelNoModel')}</strong> {t('#AgentPanelNoModelText')}</>}</p>
          <div className="agent-sim-actions">
            <button type="button" className="agent-sim-button" onClick={() => void session.simulate(scene.hasRooms ? t('#AgentPanelSuggestFurnish') : t('#AgentPanelSimulateLook'))}><Icon name="aiStar" size={16} />{t('#AgentPanelSimulate')}</button>
            <button type="button" className="agent-sim-button" onClick={() => demoBots.set(true)}><Icon name="multiplayer" size={16} />{t('#AgentPanelLiveDemo')}</button>
            <button type="button" className="agent-sim-button" onClick={() => void session.checkHealth()}><Icon name="stepCheck" size={16} />{t('#AgentPanelHealthCheck')}</button>
          </div>
        </div>
      </div> : <div className="agent-turns">
        {state.turns.map((turn, index) => {
          const previous = state.turns[index - 1];
          const stamp = !previous || (turn.role === 'user' && turn.startedAt - (previous.finishedAt ?? previous.startedAt) >= STAMP_GAP_MS);
          return <div key={turn.id} className="agent-turn">
            {stamp && <p className="agent-stamp">{stampLabel(turn.startedAt)}</p>}
            <Turn turn={turn} session={session} onSettings={() => setSettingsOpen(true)} />
          </div>;
        })}
      </div>}
    </div>}
    {!listView && <form className="agent-composer" onSubmit={event => { event.preventDefault(); submit(); }}>
      <div className="agent-input" onClick={event => { if (event.target === event.currentTarget) textarea.current?.focus(); }}>
        {attachments.length > 0 && <div className="agent-attachments">{attachments.map((src, index) => <span key={index} className="agent-attachment"><img src={src} alt={t('#AgentPanelViewSnapshot')} /><button type="button" aria-label={t('#AgentPanelRemoveSnapshot')} title={t('#AgentPanelRemoveSnapshot')} onClick={() => setAttachments(list => list.filter((_, at) => at !== index))}><Icon name="dismiss" size={12} /></button></span>)}</div>}
        <textarea ref={textarea} rows={2} value={draft} aria-label={t('#AgentPanelTaskLabel')} placeholder={running ? t('#AgentPanelWorking') : t('#AgentPanelTaskPlaceholder')}
          onChange={event => session.setDraft(event.target.value)} onKeyDown={keyDown} />
        <div className="agent-toolbar">
          <button type="button" className="agent-tool-btn" aria-label={t('#AgentPanelAttachView')} title={t('#AgentPanelShowView')} disabled={running || capturing} aria-busy={capturing} onClick={() => void capture()}>{capturing ? <span className="agent-spinner" /> : <Icon name="addImage" size={20} />}</button>
          <span className="grow" />
          {running
            ? <button type="button" className="agent-round agent-stop" aria-label={t('#AgentPanelStopAgent')} title={t('#AgentPanelStop')} onClick={() => session.stop()}><span className="agent-stop-mark" /></button>
            : <button type="submit" className="agent-round agent-send" aria-label={t('Send')} title={t('#AgentPanelSendEnter')} disabled={!canSend}><Icon name="sendArrow" size={18} /></button>}
        </div>
      </div>
    </form>}
    {settingsOpen && <AgentSettingsDialog session={session} onClose={() => setSettingsOpen(false)} />}
    <AgentResize />
  </section>;
}

/**
 * The column's right edge: a drag sets its width, a double click brings back the default and the arrow keys step it. The
 * width kept in this browser is the user's; the editor's `--agent-width` shows it fitted to the window, with no render per move.
 */
function AgentResize() {
  const edge = useRef<HTMLDivElement>(null);
  const [kept] = useState(storedAgentWidth);
  const width = useRef(kept);
  const drag = useRef<{ pointer: number; from: number; width: number } | null>(null);
  const shown = () => fitAgentWidth(width.current, window.innerWidth);
  const apply = (next: number) => {
    width.current = clampAgentWidth(next);
    const element = edge.current, fitted = shown();
    element?.closest<HTMLElement>('.editor')?.style.setProperty('--agent-width', `${fitted}px`);
    element?.setAttribute('aria-valuenow', String(fitted));
    element?.setAttribute('aria-valuemax', String(fitAgentWidth(AGENT_WIDTH.max, window.innerWidth)));
  };
  const settle = (next: number) => { apply(next); storeAgentWidth(width.current); };
  useLayoutEffect(() => {
    const refit = () => apply(width.current);
    refit();
    window.addEventListener('resize', refit);
    return () => { window.removeEventListener('resize', refit); if (drag.current) storeAgentWidth(width.current); };
  }, []);
  const end = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointer !== event.pointerId) return;
    drag.current = null;
    delete event.currentTarget.dataset.dragging;
    storeAgentWidth(width.current);
  };
  return <div ref={edge} className="agent-resize" role="separator" aria-orientation="vertical" aria-label={t('#WebAgentColumnWidth')}
    aria-valuemin={AGENT_WIDTH.min} tabIndex={0}
    onPointerDown={event => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      event.currentTarget.dataset.dragging = 'true';
      drag.current = { pointer: event.pointerId, from: event.clientX, width: shown() };
    }}
    onPointerMove={event => { const at = drag.current; if (at?.pointer === event.pointerId) apply(dragAgentWidth(at.width, at.from, event.clientX)); }}
    onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}
    onDoubleClick={() => settle(AGENT_WIDTH.initial)}
    onKeyDown={event => {
      const step = event.shiftKey ? 64 : 16;
      const next = event.key === 'ArrowLeft' ? shown() - step : event.key === 'ArrowRight' ? shown() + step
        : event.key === 'Home' ? AGENT_WIDTH.min : event.key === 'End' ? AGENT_WIDTH.max : null;
      if (next === null) return;
      event.preventDefault();
      event.stopPropagation();
      settle(next);
    }} />;
}

/** «Сегодня · Вчера · На этой неделе · Раньше»: the chats by the day of their last words. */
function chatSections(chats: SavedChat[], now = Date.now()): { label: string; chats: SavedChat[] }[] {
  const day = (value: number) => { const date = new Date(value); return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(); };
  const today = day(now);
  const sections = [{ label: t('#Today'), chats: [] as SavedChat[] }, { label: t('#Yesterday'), chats: [] as SavedChat[] }, { label: t('#ThisWeek'), chats: [] as SavedChat[] }, { label: t('#AgentPanelEarlier'), chats: [] as SavedChat[] }];
  for (const chat of [...chats].sort((a, b) => b.updatedAt - a.updatedAt)) {
    const age = Math.round((today - day(chat.updatedAt)) / 86_400_000);
    sections[age <= 0 ? 0 : age === 1 ? 1 : age < 7 ? 2 : 3].chats.push(chat);
  }
  return sections.filter(section => section.chats.length);
}

/** The design's «Chats»: «Новый чат», then this project's chats by day, each with its title, its last words and a menu. */
function ChatList({ session, state, onSettings, onHealth }: { session: AgentSession; state: AgentSessionState; onSettings: () => void; onHealth: () => void }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const sections = useMemo(() => chatSections(state.chats), [state.chats]);
  const commit = () => { if (renaming) session.renameChat(renaming, draft); setRenaming(null); };
  return <div className="agent-chats">
    <button type="button" className="agent-new-chat" onClick={() => session.newChat()}><Icon name="newChat" size={20} /><span>{t('#AgentPanelNewChat')}</span></button>
    {!state.chats.length && <p className="agent-fineprint agent-chats-empty">{t('#AgentPanelChatsEmpty')}</p>}
    {sections.map(section => <section key={section.label} className="agent-chats-section" aria-label={section.label}>
      <h3>{section.label}</h3>
      {section.chats.map(chat => <div key={chat.id} className={`agent-chat-row${chat.id === state.chatId ? ' is-current' : ''}`}>
        {renaming === chat.id
          ? <input className="agent-chat-rename" autoFocus value={draft} aria-label={t('#AgentPanelChatName')} onChange={event => setDraft(event.target.value)} onBlur={commit}
            onKeyDown={event => { event.stopPropagation(); if (event.key === 'Enter') { event.preventDefault(); commit(); } else if (event.key === 'Escape') { event.preventDefault(); setRenaming(null); } }} />
          : <button type="button" className="agent-chat-open" aria-current={chat.id === state.chatId || undefined} onClick={() => session.openChat(chat.id)}><strong>{chat.title}</strong>{chat.preview && <span>{chat.preview}</span>}</button>}
        <ActionMenu label={t('#AgentPanelChatActions', chat.title)} width={200} trigger={<Icon name="more" size={20} />} triggerClassName="agent-chat-menu"
          items={[{ id: 'rename', label: t('Rename'), icon: <Icon name="pencil" size={16} />, onSelect: () => { setDraft(chat.title); setRenaming(chat.id); } },
            { id: 'delete', label: t('#AgentPanelDeleteChat'), icon: <Icon name="trash" size={16} />, danger: true, onSelect: () => session.deleteChat(chat.id) }]} />
      </div>)}
    </section>)}
    <div className="agent-list-foot">
      <button type="button" className="agent-settings-link" onClick={onSettings}><Icon name="sliders" size={18} /><span>{t('#AgentPanelSettings')}</span></button>
      <button type="button" className="agent-settings-link" disabled={state.status === 'running'} onClick={onHealth}><Icon name="stepCheck" size={18} /><span>{t('#AgentPanelHealthCheck')}</span></button>
    </div>
  </div>;
}

/** «Работал 12 с», «Остановлен 3 с», «Ошибка»: how a finished answer ended and how long it took. */
function workedLabel(turn: ChatTurn): string {
  const time = turn.finishedAt ? elapsedLabel(turn) : '';
  if (turn.status === 'stopped') return time ? t('#AgentPanelStoppedTime', time) : t('#AgentPanelStopped');
  if (turn.status === 'error') return time ? t('#AgentPanelErrorTime', time) : t('Error');
  return time ? t('#AgentPanelWorkedTime', time) : t('#AgentPanelWorked');
}

function Turn({ turn, session, onSettings }: { turn: ChatTurn; session: AgentSession; onSettings: () => void }) {
  if (turn.role === 'user') {
    return <div className="agent-msg user"><div className="agent-bubble">
      {turn.attachments?.map((src, index) => <img key={index} className="agent-bubble-image" src={src} alt={t('#AgentPanelViewSnapshot')} />)}
      {turn.parts.map((part, index) => part.kind === 'text' && part.text ? <p key={index}>{part.text}</p> : null)}
    </div></div>;
  }
  const streaming = turn.status === 'streaming';
  const edits = turn.edits ?? 0;
  // Everything up to the last tool call is the work; what the agent says after it is the answer.
  const lastTool = turn.parts.map(part => part.kind).lastIndexOf('tool');
  const steps = lastTool >= 0 ? turn.parts.slice(0, lastTool + 1) : turn.parts.filter(part => part.kind === 'thinking');
  const answer = lastTool >= 0 ? turn.parts.slice(lastTool + 1) : turn.parts.filter(part => part.kind !== 'thinking');
  const hasAnswer = answer.some(part => part.kind === 'text' && part.text.trim());
  const who = turn.role === 'external' ? `${turn.agentName ?? t('#AgentExternalName')} · ` : '';
  return <div className={`agent-msg ${turn.role}`} aria-busy={streaming}>
    {streaming
      ? <Working parts={steps} who={who} thinking={!hasAnswer} />
      : <>
        <Steps parts={steps} open={turn.expanded} summary={who + workedLabel(turn) + (projectUnchanged(turn) ? ` · ${t('#AgentPanelProjectNotChanged')}` : '')} />
        <hr className="agent-divider" />
      </>}
    {answer.map((part, index) => <Part key={(part as { id?: string }).id ?? index} part={part} />)}
    {turn.error && <div className="agent-error" role="alert"><span>{turn.error.message}</span><div className="agent-error-actions">
      {turn.error.retryable && <button type="button" className="text-button" onClick={() => void session.retry()}>{t('#AgentPanelRetry')}</button>}
      {turn.error.notConfigured && <button type="button" className="text-button" onClick={onSettings}>{t('Settings')}</button>}
    </div></div>}
    {turn.role === 'assistant' && !streaming && edits > 0 && <div className="agent-turn-actions">
      {turn.rolledBack ? <span className="agent-note"><Icon name="undo" size={14} />{t('#AgentPanelRolledBack')}</span>
        : <button type="button" className="text-button agent-rollback" onClick={() => session.rollback(turn.id)}><Icon name="undo" size={15} />{edits > 1 ? t('#AgentPanelRollbackSteps', stepsLabel(edits)) : t('#AgentPanelRollback')}</button>}
    </div>}
    {turn.rollbackNote && <p className="agent-fineprint agent-rollback-note" role="status">{turn.rollbackNote}</p>}
  </div>;
}

/** What the launcher's status strip says about the last answer: the step running now, or the outcome and the first words of the answer. */
export function turnStatusLine(turn: ChatTurn): string {
  const tools = turn.parts.filter((part): part is Extract<ChatPart, { kind: 'tool' }> => part.kind === 'tool');
  if (turn.status === 'streaming') {
    const active = tools.find(part => part.status === 'running' || part.status === 'pending');
    return active ? (active.agent ? `${active.agent}: ` : '') + (active.progress ?? TOOL_PROGRESS[active.name] ?? active.name) + '…' : tools.length ? t('#AgentPanelStepsThinking', stepsLabel(tools.length)) : t('#AgentPanelThinking');
  }
  const lastTool = turn.parts.map(part => part.kind).lastIndexOf('tool');
  const answer = turn.parts.slice(lastTool + 1).filter((part): part is Extract<ChatPart, { kind: 'text' }> => part.kind === 'text').map(part => part.text).join(' ').replace(/\s+/g, ' ').trim();
  const excerpt = answer.length > 96 ? answer.slice(0, 95).replace(/\s+\S*$/, '') + '…' : answer;
  if (turn.status === 'error') return turn.error?.message ?? t('Error');
  if (turn.status === 'stopped') return t('#AgentPanelStoppedStatus');
  const steps = tools.length ? stepsLabel(tools.length) : '';
  const failed = tools.filter(part => part.status === 'error').length;
  return [failed ? t('#AgentPanelFinishedFailed', failed) : t('Done'), steps, turn.finishedAt ? elapsedLabel(turn) : '', excerpt].filter(Boolean).join(' · ');
}

/** The run in progress, as the design shows it: «Работаю над запросом…» over the steps, done ones ticked, the current one turning. */
function Working({ parts, who, thinking }: { parts: ChatPart[]; who: string; thinking: boolean }) {
  const tools = parts.filter((part): part is Extract<ChatPart, { kind: 'tool' }> => part.kind === 'tool');
  return <div className="agent-working" role="status">
    <p className="agent-working-title">{who}{t('#AgentPanelWorkingOnRequest')}</p>
    {tools.length > 0 && <PartsWithAgents parts={tools} />}
    {!tools.length && thinking && <span className="agent-typing" aria-label={t('#AgentPanelAgentThinking')}><i /><i /><i /></span>}
  </div>;
}

/**
 * The parts in the order they came, every step of a delegated agent in that agent's one card (the design's
 * «Layout Agent · done» cards), placed where the agent first spoke: agents working side by side interleave their
 * steps, and each card still grows by its own. Steps of the agent itself stay a plain list.
 */
function PartsWithAgents({ parts }: { parts: ChatPart[] }) {
  type Tool = Extract<ChatPart, { kind: 'tool' }>;
  const blocks: Array<{ key: string; agent?: string; steps?: Tool[]; part?: ChatPart }> = [];
  const cards = new Map<string, Tool[]>();
  let run: Tool[] | null = null;
  parts.forEach((part, index) => {
    if (part.kind === 'tool' && part.agent) {
      const card = cards.get(part.agent);
      if (card) card.push(part);
      else { const steps = [part]; cards.set(part.agent, steps); blocks.push({ key: 'agent-' + part.agent, agent: part.agent, steps }); }
      return;
    }
    if (part.kind === 'tool') {
      if (!run) { run = []; blocks.push({ key: 'steps-' + part.id, steps: run }); }
      run.push(part);
      return;
    }
    run = null;
    if (part.text.trim()) blocks.push({ key: 'part-' + index, part });
  });
  return <div className="agent-subagents">{blocks.map(block => block.agent
    ? <SubagentCard key={block.key} name={block.agent} steps={block.steps!} />
    : block.steps ? <ul key={block.key} className="agent-step-list">{block.steps.map(part => <StepRow key={part.id} part={part} />)}</ul>
    : <Part key={block.key} part={block.part!} />)}</div>;
}

function SubagentCard({ name, steps }: { name: string; steps: Extract<ChatPart, { kind: 'tool' }>[] }) {
  const busy = steps.some(step => step.status === 'pending' || step.status === 'running'), failed = steps.some(step => step.status === 'error');
  // The roster's agents wear their own pictures; any other agent its colour and the star.
  const known = roleNamed(name);
  return <div className="agent-subagent" data-status={busy ? 'working' : failed ? 'error' : 'done'} style={{ '--agent-color': known?.color ?? NEUTRAL_AGENT_COLOR } as CSSProperties}>
    <div className="agent-subagent-head">{known
      ? <img className="agent-subagent-icon" src={known.icon} width={16} height={16} alt="" aria-hidden="true" />
      : <span className="agent-subagent-mark" aria-hidden="true"><Icon name="aiStar" size={12} /></span>}
      <strong>{name}</strong><span className="agent-subagent-status">{busy ? t('#AgentPanelSubagentWorking') : failed ? t('#AgentPanelSubagentFailed') : t('#AgentPanelSubagentDone')}</span></div>
    <ul className="agent-step-list">{steps.map(part => <StepRow key={part.id} part={part} />)}</ul>
  </div>;
}

function StepRow({ part }: { part: Extract<ChatPart, { kind: 'tool' }> }) {
  const busy = part.status === 'pending' || part.status === 'running';
  const caption = busy ? (part.progress ?? TOOL_PROGRESS[part.name] ?? part.name) + '…' : part.summary ?? part.name;
  return <li className={`agent-step is-${part.status}`} aria-busy={busy}>
    <span className="agent-step-icon">{busy ? <Icon name="spin" size={16} /> : part.status === 'done' ? <Icon name="stepCheck" size={16} /> : <Icon name="info" size={14} />}</span>
    <span className="agent-step-body"><span>{caption}</span>
      {part.status === 'error' && part.detail && <span className="agent-tool-detail">{part.detail}</span>}
      {part.image && <img className="agent-tool-image" src={part.image} alt={t('#AgentPanelAgentSnapshot')} loading="lazy" />}</span>
  </li>;
}

/** A step Stop cancelled, in whichever language the chat was saved. */
const cancelledStep = (part: Extract<ChatPart, { kind: 'tool' }>): boolean => isTextOf('#AgentActionCancelled', part.summary);

/** The finished work behind an answer: the design's «Worked 2m 43s» line, which opens onto the steps and the agent's asides. */
function Steps({ parts, summary, open: initiallyOpen = false }: { parts: ChatPart[]; summary: string; open?: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const tools = parts.filter((part): part is Extract<ChatPart, { kind: 'tool' }> => part.kind === 'tool');
  // A step Stop cancelled did not fail: only real failures are counted.
  const failed = tools.filter(part => part.status === 'error' && !cancelledStep(part)).length;
  if (!parts.length) return <p className="agent-worked-line">{summary}</p>;
  return <details className="agent-steps" open={open} onToggle={event => setOpen((event.currentTarget as HTMLDetailsElement).open)}>
    <summary><span className="agent-steps-label">{summary}{tools.length ? ` · ${failed ? t('#AgentPanelStepsWithFailed', stepsLabel(tools.length), failed) : stepsLabel(tools.length)}` : ''}</span><Icon name="chevronDown" size={14} /></summary>
    <div className="agent-steps-body"><PartsWithAgents parts={parts} /></div>
  </details>;
}

function Part({ part }: { part: ChatPart }) {
  const label: ReactNode = part.agent ? <span className="agent-part-agent">{part.agent}</span> : null;
  if (part.kind === 'text') return part.text.trim() ? <p className="agent-text">{label}{richText(part.text)}</p> : null;
  if (part.kind === 'thinking') return part.text.trim() ? <details className="agent-thinking"><summary>{t('#AgentPanelReasoning')}</summary><p>{part.text}</p></details> : null;
  return <ul className="agent-step-list"><StepRow part={part} /></ul>;
}

/**
 * The agents' pointers over the stage, where the demo presence does not draw them: every agent outside the roster (the
 * bridge, the local agent), and the subagents where there is no presence (a shared project). A pointer with nowhere to
 * stand waits in one row by the dock. The view pans to an agent's work once a run, never during the user's gesture.
 */
export function AgentCursorOverlay() {
  const cursors = useSyncExternalStore(agentCursor.subscribe, agentCursor.all, agentCursor.all);
  const presence = useSyncExternalStore(demoBots.subscribe, demoBots.current, demoBots.current);
  // A wall plan shows one face, where no point of the floor stands: every agent waits in the row there.
  const wallPlan = useStore(s => s.mode === 'plan' && s.planSurface === 'wall' && !!s.wallView);
  useRevealOnce(cursors);
  const drawn = cursors.filter(cursor => wallPlan || !presence || !roleById(cursor.key) || !cursor.point);
  const parked = drawn.filter(cursor => wallPlan || !cursor.point);
  return <>
    {drawn.filter(cursor => !wallPlan && cursor.point).map(cursor => <AgentCursorView key={cursor.key} cursor={cursor} />)}
    {parked.length > 0 && <div className="agent-cursor-row" aria-hidden="true">{parked.map(cursor => <AgentCursorView key={cursor.key} cursor={cursor} />)}</div>}
  </>;
}

/** Brings an agent's work into view once while the stage holds agents, and never while the user drags, pans or pinches. */
function useRevealOnce(cursors: readonly CursorState[]) {
  const revealed = useRef(false), pressed = useRef(new Set<number>());
  useEffect(() => {
    // A release the page never sees (a context menu, ⌘-Tab) is healed by the menu opening, the pointer's next release or leaving the window.
    const down = (event: globalThis.PointerEvent) => { pressed.current.add(event.pointerId); }, up = (event: globalThis.PointerEvent) => { pressed.current.delete(event.pointerId); };
    const clear = () => { pressed.current.clear(); };
    window.addEventListener('pointerdown', down, true); window.addEventListener('pointerup', up, true); window.addEventListener('pointercancel', up, true); window.addEventListener('contextmenu', clear, true); window.addEventListener('blur', clear);
    return () => { window.removeEventListener('pointerdown', down, true); window.removeEventListener('pointerup', up, true); window.removeEventListener('pointercancel', up, true); window.removeEventListener('contextmenu', clear, true); window.removeEventListener('blur', clear); };
  }, []);
  useEffect(() => {
    if (!cursors.length) { revealed.current = false; return; }
    const cursor = cursors.find(candidate => candidate.reveal && candidate.point);
    if (revealed.current || !cursor?.point || pressed.current.size || store.get().isDragging) return;
    const editor = editorAccess();
    if (!editor) return;
    revealed.current = true;
    const area = editor.visibleArea();
    if (store.get().mode === 'plan') editor.plan()?.revealPoint(cursor.point, area); else editor.scene3d()?.revealPoint(cursor.point, area);
  }, [cursors]);
}

function AgentCursorView({ cursor }: { cursor: CursorState }) {
  const mode = useStore(s => s.mode);
  const [spot, setSpot] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!cursor.visible) { setSpot(null); return; }
    const place = () => {
      const editor = editorAccess();
      const stage = document.querySelector('.stage')?.getBoundingClientRect();
      if (!cursor.point || !editor || !stage) { setSpot(null); return; }
      const at = store.get().mode === 'plan' ? editor.plan()?.screenPointAt(cursor.point) : editor.scene3d()?.screenPointAt(cursor.point);
      if (!at) { setSpot(null); return; }
      setSpot({ x: Math.max(12, Math.min(stage.width - 12, at.x - stage.left)), y: Math.max(12, Math.min(stage.height - 12, at.y - stage.top)) });
    };
    place();
    const timer = setInterval(place, 120);
    return () => clearInterval(timer);
  }, [cursor.visible, cursor.point, cursor.updatedAt, mode]);
  if (!cursor.visible) return null;
  const role = roleById(cursor.key), color = role?.color ?? NEUTRAL_AGENT_COLOR;
  return <div className={`agent-cursor collab-cursor is-agent is-${cursor.kind}${spot ? '' : ' is-parked'}${cursor.working ? ' is-busy' : ''}`} style={{ '--participant-color': color, ...(spot ? { left: spot.x, top: spot.y } : {}) } as CSSProperties} aria-hidden="true">
    <ParticipantPointer name={cursor.name} look={{ color, agent: true, role: 'editor', icon: role?.icon,
      halo: role?.halo, activity: cursor.label, working: cursor.working }} />
  </div>;
}

/** Models write **bold** and bullet lines; render those two and leave the rest as written. */
function richText(text: string): ReactNode {
  const lines = text.replace(/\r/g, '').split('\n');
  return lines.map((line, index) => {
    const bullet = /^\s*[-*•]\s+/.test(line);
    const body = bullet ? line.replace(/^\s*[-*•]\s+/, '') : line;
    const pieces = body.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((piece, at) => /^\*\*[^*]+\*\*$/.test(piece) ? <strong key={at}>{piece.slice(2, -2)}</strong> : piece);
    return <span key={index} className={bullet ? 'agent-bullet' : undefined}>{bullet ? '• ' : ''}{pieces}{index < lines.length - 1 ? '\n' : ''}</span>;
  });
}

function AgentSettingsDialog({ session, onClose }: { session: AgentSession; onClose: () => void }) {
  const state = useAgentSession(session);
  const settings = state.settings;
  const [apiKey, setApiKey] = useState(settings.apiKey ?? '');
  const [customModel, setCustomModel] = useState(() => !!settings.openai?.model && !DEEPSEEK_MODELS.some(choice => choice.id === settings.openai?.model));
  useEffect(() => { void session.probeProxyConfig(); }, [session]);
  useEffect(() => {
    const key = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [onClose]);
  const saveKey = () => session.updateSettings({ apiKey: apiKey.trim() || undefined });
  const mcpCommand = 'claude mcp add home-planner -- npm --prefix /Users/wantbefree/Codex/HomePlannerWeb run --silent mcp';
  return <div className="modal-backdrop agent-settings-backdrop" onClick={onClose}>
    <section className="home-dialog agent-settings" role="dialog" aria-modal="true" aria-label={t('#AgentPanelSettings')} onClick={event => event.stopPropagation()}>
      <header className="agent-settings-head"><h2>{t('#AgentPanelSettings')}</h2><button type="button" className="icon-btn" aria-label={t('#AgentPanelCloseSettings')} onClick={onClose}><Icon name="close" size={18} /></button></header>
      <div className="agent-settings-body">
        <section>
          <h3>{t('#AgentPanelModel')}</h3>
          <SelectDropdown label={t('#AgentPanelBuiltInModel')} value={settings.model ?? ''} onChange={value => session.updateSettings({ model: value || undefined })}
            options={[{ value: '', label: t('#AgentPanelModelDefault') }, ...MODEL_CHOICES.map(choice => ({ value: choice.id, label: choice.label }))]} />
          <label className="agent-check"><input type="checkbox" aria-label={t('#AgentPanelMockLabel')} checked={!!settings.mock} onChange={event => session.updateSettings({ mock: event.target.checked })} />{t('#AgentPanelMockText')}</label>
        </section>
        <section>
          <h3>{t('#AgentPanelProviderTitle')}</h3>
          <SelectDropdown label={t('#AgentPanelProvider')} value={session.effectiveProvider()} onChange={value => session.updateSettings({ provider: value as ProviderKind, providerChosen: true })} options={PROVIDER_CHOICES.map(choice => ({ value: choice.id, label: choice.label }))} />
          {!settings.providerChosen && <p className="agent-fineprint">{state.proxy ? (state.proxy.openai.configured || state.proxy.anthropic ? t('#AgentPanelProviderAuto') : t('#AgentPanelProviderNoKeys')) : t('#AgentPanelProviderChecking')}</p>}
          {session.effectiveProvider() === 'openai' && <div className="agent-form">
            <p className="agent-fineprint">{state.proxy?.openai.configured ? state.proxy.openai.baseUrl ? t('#AgentPanelProviderServerKeyAt', state.proxy.openai.baseUrl) : t('#AgentPanelProviderServerKey') : t('#AgentPanelProviderNoServerKey')}</p>
            <input aria-label={t('#AgentPanelProviderUrl')} placeholder={DEEPSEEK_BASE_URL} value={settings.openai?.baseUrl ?? ''} onChange={event => session.updateSettings({ openai: { ...settings.openai, baseUrl: event.target.value.trim() || undefined } })} />
            <SelectDropdown label={t('#AgentPanelProviderModel')} value={customModel ? '__custom' : settings.openai?.model ?? ''} onChange={value => { if (value === '__custom') { setCustomModel(true); return; } setCustomModel(false); session.updateSettings({ openai: { ...settings.openai, model: value || undefined } }); }}
              options={[{ value: '', label: t('#AgentPanelDefaultModel', DEEPSEEK_MODELS[0].label) }, ...DEEPSEEK_MODELS.map(choice => ({ value: choice.id, label: choice.label })), { value: '__custom', label: t('#AgentPanelOtherModel') }]} />
            {customModel && <input aria-label={t('#AgentPanelModelName')} placeholder={t('#AgentPanelModelNamePlaceholder')} value={settings.openai?.model ?? ''} onChange={event => session.updateSettings({ openai: { ...settings.openai, model: event.target.value.trim() || undefined } })} />}
            <label className="agent-check"><input type="checkbox" aria-label={t('#AgentPanelThinkingModeLabel')} checked={!!settings.openai?.thinking} onChange={event => session.updateSettings({ openai: { ...settings.openai, thinking: event.target.checked } })} />{t('#AgentPanelThinkingModeText')}</label>
            <input type="password" autoComplete="off" aria-label={t('#AgentPanelProviderKey')} placeholder={t('#AgentPanelProviderKeyPlaceholder')} value={settings.openai?.apiKey ?? ''} onChange={event => session.updateSettings({ openai: { ...settings.openai, apiKey: event.target.value.trim() || undefined } })} />
          </div>}
        </section>
        <section>
          <h3>{t('#AgentPanelLocalTitle')}</h3>
          <div className="agent-row agent-switch-row">
            <span>{t('#AgentPanelLocalText')}</span>
            <button type="button" role="switch" aria-checked={settings.localAgent} aria-label={t('#AgentPanelLocalAgent')} className="inspector-toggle" onClick={() => session.updateSettings({ localAgent: !settings.localAgent })}><span /></button>
          </div>
          {settings.localAgent && <p className={`agent-fineprint agent-bridge-line is-${state.local.state === 'ready' ? 'connected' : state.local.state === 'offline' ? 'error' : 'connecting'}`}>
            {state.local.state === 'ready' ? state.local.project ? t('#AgentPanelLocalServerReadyProject', state.local.project, state.local.detail ? ` (${state.local.detail})` : '') : t('#AgentPanelLocalServerReady', state.local.detail ? ` (${state.local.detail})` : '') : state.local.state === 'offline' ? t('#AgentPanelLocalServerOffline', session.localAgentUrl, state.local.detail ? `: ${state.local.detail}` : '') : t('#AgentPanelCheckingServer')}
          </p>}
          {settings.localAgent && <div className="agent-form">
            <SelectDropdown label={t('#AgentPanelLocalModel')} value={settings.localModel ?? ''} onChange={value => session.updateSettings({ localModel: value || undefined })} options={LOCAL_MODEL_CHOICES.map(choice => ({ value: choice.id, label: choice.label }))} />
            <input aria-label={t('#AgentPanelLocalUrl')} placeholder={LOCAL_AGENT_URL} value={settings.localAgentUrl ?? ''} onChange={event => session.updateSettings({ localAgentUrl: event.target.value.trim() || undefined })} />
          </div>}
          <p className="agent-fineprint">{rich('#AgentPanelLocalLogin', <code>claude</code>, <code>/login</code>)}</p>
          <pre className="agent-code">npm run agent:local</pre>
        </section>
        <section>
          <h3>{t('#AgentPanelOwnKeyTitle')}</h3>
          <p className="agent-fineprint">{t('#AgentPanelOwnKeyText')}</p>
          <div className="agent-row">
            <input type="password" autoComplete="off" aria-label={t('#AgentPanelAnthropicKey')} placeholder="sk-ant-…" value={apiKey} onChange={event => setApiKey(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); saveKey(); } }} />
            <button type="button" className="seg" onClick={saveKey} disabled={(apiKey.trim() || '') === (settings.apiKey ?? '')}>{settings.apiKey && !apiKey.trim() ? t('Delete') : t('Save')}</button>
          </div>
        </section>
        <section>
          <h3>{t('#AgentPanelExternalTitle')}</h3>
          <div className="agent-row agent-switch-row">
            <span>{t('#AgentPanelExternalText')}</span>
            <button type="button" role="switch" aria-checked={settings.bridge} aria-label={t('#AgentPanelExternalAgents')} className="inspector-toggle" onClick={() => session.updateSettings({ bridge: !settings.bridge })}><span /></button>
          </div>
          {settings.bridge && <p className={`agent-fineprint agent-bridge-line is-${state.bridge.state}`}>{state.bridge.state === 'connected' ? t('#AgentPanelBridgeConnected') : state.bridge.state === 'connecting' ? t('#AgentPanelBridgeConnecting') : t('#AgentPanelBridgeOffline', state.bridge.detail ? `: ${state.bridge.detail}` : '')}</p>}
          <p className="agent-fineprint">{rich('#AgentPanelMcpRun', <code>npm run mcp</code>)}</p>
          <pre className="agent-code">{mcpCommand}</pre>
        </section>
      </div>
    </section>
  </div>;
}

/** The store's own view, for tests: which scene state the dock reads. */
export const agentDockScene = () => store.get().scene;
